import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { Search, ArrowRight, RefreshCw, Pencil, X } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import IdxDisclaimer from "../components/IdxDisclaimer";
import DrawSearchControl, { pointInShape } from "../components/DrawSearchControl";

const HOUSTON_CENTER = [29.7604, -95.3698];
const fmt = (n) => (n ? `$${Number(n).toLocaleString()}` : "—");

// Custom price-tag pin (branded)
const makePin = (price) => new L.DivIcon({
  className: "mls-price-pin",
  html: `<div style="
    background:#E63946;color:#fff;font-family:'Montserrat',system-ui,sans-serif;
    font-weight:700;font-size:11px;padding:4px 8px;border-radius:14px;
    box-shadow:0 2px 8px rgba(0,0,0,0.3);border:2px solid #fff;white-space:nowrap;
  ">$${(Math.round(price / 1000) || 0)}K</div>`,
  iconSize: [50, 22],
  iconAnchor: [25, 22],
});

export default function MlsMap() {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [beds, setBeds] = useState(0);
  const [shape, setShape] = useState(null); // { type, latlngs? , center?, radius? } | null
  const [clearSignal, setClearSignal] = useState(0);
  const mapRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    // Pull ALL pins from the Trestle-synced index (unlimited). Browser-side
    // clustering handles rendering perf even at 10k+ markers.
    api.get("/mls/map-pins", { params: { status: "Active", limit: 50000 } })
      .then((r) => setPins(r.data || []))
      .catch(() => setPins([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return pins.filter((p) => {
      if (query && !(
        p.address?.toLowerCase().includes(query) ||
        p.city?.toLowerCase().includes(query)
      )) return false;
      if (minPrice && p.price < Number(minPrice)) return false;
      if (maxPrice && p.price > Number(maxPrice)) return false;
      if (beds && (p.beds || 0) < beds) return false;
      if (shape && !pointInShape(p.lat, p.lng, shape)) return false;
      return true;
    });
  }, [pins, q, minPrice, maxPrice, beds, shape]);

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="mls-map-page">
      <Seo
        title="Houston MLS Map"
        description="Explore every active Houston MLS listing on an interactive map. Live HAR feed via CoreLogic Trestle."
        path="/mls-map"
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-3">Live HAR Feed · Map View</div>
        <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter text-[#1A1A1A]">
          The Houston market, <em className="not-italic text-[#E63946]">at a glance</em>.
        </h1>
        <p className="text-[#555] mt-5 max-w-2xl text-lg">
          {loading ? "Loading pins…" : `${filtered.length.toLocaleString()} of ${pins.length.toLocaleString()} active listings shown. Click any pin for details.`}
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by address or city…" className="w-full pl-9 pr-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E63946]" data-testid="mls-map-filter" />
          </div>
          <input type="number" placeholder="Min $" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className="w-28 px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid="mls-map-minprice" />
          <input type="number" placeholder="Max $" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="w-28 px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid="mls-map-maxprice" />
          <select value={beds} onChange={(e) => setBeds(Number(e.target.value))} className="px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid="mls-map-beds">
            {[0, 1, 2, 3, 4, 5].map((b) => <option key={b} value={b}>{b === 0 ? "Any beds" : `${b}+ beds`}</option>)}
          </select>
          <Link to="/mls-listings" className="ml-auto text-sm text-[#E63946] hover:underline flex items-center gap-1" data-testid="mls-map-goto-list">
            Grid view <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Draw-a-shape instructions strip */}
        <div
          className={`mb-4 flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border ${
            shape
              ? "bg-red-50/70 border-red-200"
              : "bg-stone-100/60 border-stone-200"
          }`}
          data-testid="mls-map-draw-strip"
        >
          <Pencil className="w-4 h-4 text-[#E63946] shrink-0" />
          <div className="text-sm text-[#1A1A1A] flex-1 min-w-[220px]">
            {shape ? (
              <>
                <b>{filtered.length}</b> listing{filtered.length === 1 ? "" : "s"} inside your{" "}
                <span className="uppercase tracking-wider text-xs bg-[#E63946] text-white px-1.5 py-0.5 rounded">
                  {shape.type}
                </span>{" "}
                shape.
              </>
            ) : (
              <>
                <b>Draw a shape to search.</b> Use the toolbar on the top-right of the map to draw a polygon, rectangle, or circle — only listings inside will be shown.
              </>
            )}
          </div>
          {shape && (
            <button
              onClick={() => { setShape(null); setClearSignal((n) => n + 1); }}
              className="inline-flex items-center gap-1.5 bg-white border border-red-200 hover:border-[#E63946] hover:text-[#E63946] text-[#1A1A1A] px-3 py-1.5 rounded-md text-xs font-semibold"
              data-testid="mls-map-clear-shape"
            >
              <X className="w-3.5 h-3.5" /> Clear shape
            </button>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden h-[680px] relative" data-testid="mls-map-container">
          {loading && (
            <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-white/60 backdrop-blur-sm">
              <RefreshCw className="w-6 h-6 animate-spin text-[#E63946]" />
            </div>
          )}
          <MapContainer center={HOUSTON_CENTER} zoom={10} scrollWheelZoom style={{ height: "100%", width: "100%" }} whenCreated={(m) => (mapRef.current = m)}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MarkerClusterGroup chunkedLoading maxClusterRadius={60} spiderfyOnMaxZoom>
              {filtered.map((p) => (
                <Marker key={p.listing_key} position={[p.lat, p.lng]} icon={makePin(p.price)}>
                  <Popup>
                    <div className="w-56" data-testid={`mls-map-popup-${p.listing_key}`}>
                      {p.photos?.[0] && (
                        <img src={p.photos[0]} alt={p.address} className="w-full h-28 object-cover rounded-md mb-2" />
                      )}
                      <div className="font-display text-lg font-semibold text-[#1A1A1A]">{fmt(p.price)}</div>
                      <div className="text-[11px] text-gray-600 mt-0.5">{p.address}</div>
                      <div className="text-[10px] text-gray-500">{p.city}, TX</div>
                      <div className="text-[10px] text-gray-500 mt-1">
                        {p.beds} bd · {p.baths} ba · {p.sqft?.toLocaleString?.() || p.sqft} sqft
                      </div>
                      <Link to={`/mls-listings/${p.listing_key}`} className="mt-2 inline-flex items-center gap-1 text-[#E63946] text-xs font-semibold hover:gap-2 transition-all" data-testid={`mls-map-popup-link-${p.listing_key}`}>
                        View details <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
            <DrawSearchControl onShape={setShape} clearSignal={clearSignal} />
          </MapContainer>
        </div>

        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-3 text-center">
          Map data © OpenStreetMap contributors · IDX data © Houston Association of REALTORS® · Refreshed nightly
        </p>
        <IdxDisclaimer />
      </section>
    </div>
  );
}
